import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  CreateCurrencyExchangeRateForClientDocument,
  type CreateCurrencyExchangeRateForClientMutation,
  type CreateCurrencyExchangeRateForClientMutationVariables,
  CreateCurrencyExchangeRateForWorkspaceDocument,
  type CreateCurrencyExchangeRateForWorkspaceMutation,
  type CreateCurrencyExchangeRateForWorkspaceMutationVariables,
  type ExchangeRateFragment,
  UpdateCurrencyExchangeRateForClientDocument,
  type UpdateCurrencyExchangeRateForClientMutation,
  type UpdateCurrencyExchangeRateForClientMutationVariables,
  UpdateCurrencyExchangeRateForWorkspaceDocument,
  type UpdateCurrencyExchangeRateForWorkspaceMutation,
  type UpdateCurrencyExchangeRateForWorkspaceMutationVariables,
} from "@src/__generated__/urql-graphql";
import { FormRow, NumberInput, Select } from "@src/components/ui-kit";
import { CancelButton } from "@src/components/ui-kit/Button/CancelButton";
import { Icon } from "@src/components/ui-kit/Icon";
import { client } from "@src/services/client";
import { dateTime } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { omit } from "lodash";
import { observer } from "mobx-react-lite";
import React, { type FC, type ReactNode, useEffect, useMemo } from "react";
import { Form } from "./Form";
import ModalDatePicker from "./ModalDatePicker";

type ModalExchangeRateProps = {
  modalId?: string;
  title: ReactNode;
  onSubmit?: (exchangeRate: ExchangeRateFragment) => void;

  valid_from?: Date;
  valid_to?: Date;

  /** Denominated value */
  exchange_rate?: string | number;
  exchangeCurrencyId?: string;
} & (
  | { source: "client"; clientId: string; exchangeRateId?: undefined }
  | { source: "client"; clientId?: undefined; exchangeRateId: string }
  | { source: "workspace"; clientId?: undefined; exchangeRateId?: undefined }
  | { source: "workspace"; clientId?: undefined; exchangeRateId: string }
);

const ModalExchangeRate: FC<ModalExchangeRateProps> = ({
  modalId,
  title,
  onSubmit,
  valid_from,
  valid_to,
  exchange_rate,
  clientId,
  source,
  exchangeRateId,
  exchangeCurrencyId,
}) => {
  const appStore = useStore();
  const form = useMemo(() => new Form(appStore, exchangeCurrencyId), []);
  const datePickerModal = useDisclosure();
  const [isLoading, setIsLoading] = useBoolean();

  const currencyOptions = useMemo(() => {
    return appStore.workspaceStore.currencies
      .filter((i) => i.id !== appStore.workspaceStore.settings?.currency.code)
      .map((i) => ({ label: i.code, value: i.id }));
  }, [appStore.workspaceStore.currencies]);

  const currentCurrencyCode = useMemo(() => {
    return (
      appStore.workspaceStore.currencies.find(
        (i) => i.id === form.currency_id.value,
      )?.code || ""
    );
  }, [form.currency_id.value]);

  const create = async () => {
    setIsLoading.on();
    const { data } =
      source === "client"
        ? await client
            .mutation<
              CreateCurrencyExchangeRateForClientMutation,
              CreateCurrencyExchangeRateForClientMutationVariables
            >(CreateCurrencyExchangeRateForClientDocument, {
              input: { client_id: clientId!, ...form.serialize() },
            })
            .toPromise()
        : await client
            .mutation<
              CreateCurrencyExchangeRateForWorkspaceMutation,
              CreateCurrencyExchangeRateForWorkspaceMutationVariables
            >(CreateCurrencyExchangeRateForWorkspaceDocument, {
              input: form.serialize(),
            })
            .toPromise();

    setIsLoading.off();
    if (data && "createCurrencyExchangeRate" in data) {
      onSubmit?.(data.createCurrencyExchangeRate);
    }
    if (data && "createExchangeRateForWorkspace" in data) {
      onSubmit?.(data.createExchangeRateForWorkspace);
    }
    onClose();
  };

  const update = async () => {
    setIsLoading.on();
    const { data } =
      source === "client"
        ? await client
            .mutation<
              UpdateCurrencyExchangeRateForClientMutation,
              UpdateCurrencyExchangeRateForClientMutationVariables
            >(UpdateCurrencyExchangeRateForClientDocument, {
              input: {
                id: exchangeRateId!,
                ...omit(form.serialize(), "currency_id"),
              },
            })
            .toPromise()
        : await client
            .mutation<
              UpdateCurrencyExchangeRateForWorkspaceMutation,
              UpdateCurrencyExchangeRateForWorkspaceMutationVariables
            >(UpdateCurrencyExchangeRateForWorkspaceDocument, {
              input: {
                id: exchangeRateId!,
                ...omit(form.serialize(), "currency_id"),
              },
            })
            .toPromise();

    setIsLoading.off();
    if (data && "updateCurrencyExchangeRate" in data) {
      onSubmit?.(data.updateCurrencyExchangeRate);
    }
    if (data && "updateExchangeRateForWorkspace" in data) {
      onSubmit?.(data.updateExchangeRateForWorkspace);
    }
    onClose();
  };

  const onSave = async () => {
    if (exchangeRateId) {
      // Update
      const { hasError } = await form.validate("update");

      if (hasError) return;

      update();
    } else {
      // Create
      const { hasError } = await form.validate("create");

      if (hasError) return;

      create();
    }
  };

  const onClose = () => {
    if (!modalId) return;
    appStore.UIStore.dialogs.closeModal(modalId);
  };

  useEffect(() => {
    // Populate form on open
    valid_from && form.valid_from.onChange(valid_from);
    valid_to && form.valid_to.onChange(valid_to);
    exchange_rate &&
      form.exchange_rate.onChange(
        form.exchange_rate.value.setDenomValue(exchange_rate),
      );
  }, []);

  return (
    <React.Fragment>
      <Modal isOpen onClose={onClose} size="2xl" trapFocus={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!exchangeRateId && (
              <FormRow title={<Trans>Currency</Trans>}>
                <Select
                  asPortal
                  {...fieldToSelectProps(form.currency_id, currencyOptions)}
                />
              </FormRow>
            )}

            <FormRow
              title={
                <div className="flex justify-between relative -mr-3">
                  <Trans>Rate</Trans>
                  <span className="text-gray-600">
                    1 {appStore.workspaceStore.settings?.currency_symbol} =
                  </span>
                </div>
              }
            >
              <NumberInput
                currency
                suffix={currentCurrencyCode}
                {...fieldNumberModelToInputProps(form.exchange_rate)}
              />
            </FormRow>

            <FormRow title={<Trans>Rate validity</Trans>}>
              <Button
                w="full"
                onClick={datePickerModal.onOpen}
                rightIcon={<Icon name="chevron-down" color="white" />}
              >
                {(form.valid_from.value &&
                  form.valid_to.value &&
                  `${dateTime.format(form.valid_from.value)} - ${dateTime.format(
                    form.valid_to.value,
                  )}`) ||
                  (form.valid_from.value &&
                    `${t`From`} - ${dateTime.format(form.valid_from.value)}`) ||
                  (form.valid_to.value &&
                    `${t`Until`} - ${dateTime.format(form.valid_to.value)}`) ||
                  t`Choose date range`}
              </Button>
            </FormRow>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup spacing="3">
              <CancelButton onClick={onClose} />
              <Button isLoading={isLoading} onClick={onSave}>
                <Trans>Save</Trans>
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalDatePicker
        {...datePickerModal}
        from={form.valid_from.value}
        to={form.valid_to.value}
        onSubmit={(range) => {
          form.valid_from.onChange(range.start);
          form.valid_to.onChange(range.end);
        }}
      />
    </React.Fragment>
  );
};

export default observer(ModalExchangeRate);
